import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {EMPTY, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, debounceTime, filter, map, switchMap, tap} from 'rxjs/operators';
import {UntypedFormControl} from '@angular/forms';
import {MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {SearchResult, SearchStatus} from 'src/app/shared/models/search.model';
import {EntityType} from 'src/app/shared/models/entity-type.enum';
import {Logger, LoggerService} from 'src/app/shared/services/logger.service';
import {StoreBrandingService} from 'src/app/shared/services/client-storage-services/internal-storage-services/store-branding.service';
import { AuthStorageService } from 'src/app/auth/auth-storage.service';
import { alphaNumericWithSpecialCharsRegex } from 'src/app/shared/operators/regex-operators';
import { GlobalSearchService } from './global-search.service';

class AutoCompleteOption {
  entityType: EntityType;
  entityName: string;
  path: string[];
  entityId: number;
}

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss']
})
export class GlobalSearchComponent implements OnInit {
  searchResults$: Observable<SearchResult>;
  groupedSearchResults: { [entityType: string]: AutoCompleteOption[] } = {};
  searchInput = new UntypedFormControl();
  searchInputOnSelected: string;
  @Output() toggleSidenav = new EventEmitter<void>();
  searchQueryChanged: Subject<string> = new Subject<string>();
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @ViewChild('search') search: ElementRef;

  lastSearchResult: any;
  lastSearchedValue: string;
  subscrAutoComplete: Subscription;

  private logger: Logger;

  constructor(
    private router: Router,
    private searchService: GlobalSearchService,
    private loggerFactory: LoggerService,
    public authStorageService: AuthStorageService,
    private storeBrandingService: StoreBrandingService,
    private elementRef: ElementRef
  ) {
    this.logger = this.loggerFactory.getLogger("HeaderComponent");
  }

  ngOnInit() {
    this.searchResults$ = this.searchInput.valueChanges.pipe(
      tap((val) => {
        console.log(`searchInput value ${val}`)
      }),
      map((searchVal) => {
        if (searchVal.length >= 3) {
          return searchVal;
        } else {
          this.logger.debug("search value too short, closing autocomplete")
          this.groupedSearchResults = {};
        }
        return undefined;
      }),
      debounceTime(500),
      filter((s) => s !== undefined && alphaNumericWithSpecialCharsRegex().test(this.searchInput.value)),
      tap((val) => {
        console.log(`searchInput distinct value ${val}`)
      }),
      switchMap((searchVal) => {
        if (this.lastSearchedValue === searchVal) {
          return of(this.lastSearchResult);
        }
        return this.searchService.fetchGlobalSearch(searchVal).pipe(
          catchError((e) => {
            this.logger.error("got error from search service %o", e)
            return EMPTY;
          })
        );
      })
    );

    this.subscrAutoComplete = this.searchResults$.subscribe((searchResult: SearchResult) => {
      console.log('search result %o', searchResult)
      this.lastSearchResult = searchResult;
      this.groupedSearchResults = {};

      searchResult.foundEntityItems.forEach((res) => {
        if (!this.groupedSearchResults[res.entity.type]) {
          this.groupedSearchResults[res.entity.type] = [];
        }
        const names = [];
        let root = res;
        do {
          names.push(root.entity.name);
          root = root.childEntity;
        } while (root);

        const option = new AutoCompleteOption();
        option.entityType = res.entity.type;
        option.entityId = res.entity.id;
        option.entityName = res.entity.name;
        option.path = names;
        this.groupedSearchResults[res.entity.type].push(option);
      });
    });
  }

  get createDisplayFn() {
    return (id) => {
      this.logger.debug(`search input value ${this.searchInput.value}`);
      this.searchInputOnSelected = this.searchInput.value;
      return this.searchInput.value;
    }
  }

  onOptionSelected(event) {
    const [entityType, entityId] = event.option.id.split('-|-');
    this.logger.debug(`search input value in onOptionSelected ${this.searchInput.value}`);
    this.searchInput.setValue(this.searchInputOnSelected);
    this.autocomplete.closePanel();
    this.lastSearchedValue = this.searchInput.value;
    this.search.nativeElement.blur();

    if (entityType === EntityType.CLIENT) {
      this.router.navigate(['/', EntityType.VENUE.toLowerCase(), entityId, EntityType.CLIENT.toLowerCase()]).catch();
    } else {
      this.router.navigate(['/', entityType.toLowerCase(), entityId]).catch();
    }
  }

  isErrorMessage(entityItem: AutoCompleteOption) {
    return entityItem.entityName === SearchStatus.TOO_MANY_RESULTS;
  }

  get profile$() {
    return this.authStorageService.notifyUserProfileObservable$;
  }

  get logoFile(): string {
    return this.storeBrandingService?.selectedOrgUI?.companyLogoAsUrl;
  }

  get headerTextColor(): string {
    return this.storeBrandingService?.selectedOrgUI?.themeColors?.topMenuForegroundColor;
  }

  ngOnDestroy() {
    this.subscrAutoComplete?.unsubscribe();
  }
}
